import { Component, Input, OnChanges, OnInit, SimpleChanges } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { ICvxClaimsPrincipal } from "@cvx/cal";
import { CalAngularService } from "@cvx/cal-angular";
import { PageLayout } from "@cvx/nextpage";
import { lastValueFrom } from "rxjs";
import { AppRoleConstants } from "src/app/constants/AppRole.constants";
import { csvHelper } from "src/app/helpers/exporter/csvHelper";
import { IGenericApiResponse } from "src/app/models/common/GenericApiResponse";
import { DirectoryObjectBase } from "src/app/models/directory/DirectoryObjectBase";
import { DirectoryDomain } from "src/app/models/directory/enums/DirectoryDomain.enum";
import { GroupMemberType } from "src/app/models/directory/enums/GroupMemberType.enum";
import { IIdAndDomain } from "src/app/models/directory/IIdAndDomain";
import { DirectoryUser } from "src/app/models/directory/users/DirectoryUser";
import { WorkflowRequestTypes } from "src/app/models/requests/enums/WorkflowRequestTypes.enum";
import { WorkflowStates } from "src/app/models/requests/enums/WorkflowStates.enum";
import { IWorkflowRequest } from "src/app/models/requests/WorkflowRequest";
import { DirectoryUserService } from "src/app/services/directory/DirectoryUser.service";
import { GraphApplicationService } from "src/app/services/graph/GraphApplication.service";
import { WorkflowRequestService } from "src/app/services/workflow/WorkflowRequestService";
import { DialogDirectorySearchComponent } from "../../_shared/dialog-directory-search/dialog-directory-search.component";
import { MatDialog } from "@angular/material/dialog";
import { IDialogDirectoryData } from "src/app/models/components/IDialogDirectoryData";
import { UserType } from "src/app/models/directory/enums/UserType.enum";
import { ApproverChange, ChangeRequestApprovers } from "src/app/models/requests/ChangeRequestApprovers";
import { UntypedFormControl, Validators } from "@angular/forms";
import { GraphUserService } from "src/app/services/graph/GraphUser.service";

@Component({
    selector: 'details-requests',
    templateUrl: './details-requests.component.html'
})

export class DetailsRequestsComponent implements OnInit, OnChanges {

    cancellableRequestStates: WorkflowStates[] = [
        WorkflowStates.PendingApproval
    ];
    /**
     * page rendering controls
     */
    hasRequestReaderRole: boolean = false;
    isDirectUrlToRequest: boolean = false;
    PageLayout = PageLayout;
    canCancel: boolean = false;;
    isCancelling: boolean = false;
    cancellErrorMessage = { message: '', errors: [] };
    approverChangeSubmitErrorMessage = { message: '', errors: [] };
    approverChangeErrorMessage = { message: '', errors: [] };
    isSearching: boolean = false;
    screenMessage: string = '';
    loggedIn: boolean;
    currentUserProfile: ICvxClaimsPrincipal;
    doneLoading: boolean = false;    // all elements are done loading
    doneLoadingDetails: boolean = false;
    doneLoadingApprovals: boolean = false;
    userIsRequestApproverManager: boolean = false;
    approverChangeJustification = new UntypedFormControl('', { validators: [Validators.required] });
    isSubmitting: boolean = false;
    panelOpenState = false;


    /**
     * route parameters
     */
    @Input() startSearch: boolean = false;
    @Input() type: WorkflowRequestTypes;
    @Input() id: string;
    @Input() showTitle: boolean = true;
    /**
     * loaded data
     */
    workflowRequest: IWorkflowRequest;
    requestor: DirectoryObjectBase[] = []; // this doesn't technically need to be an array
    approvers: DirectoryObjectBase[] = [];
    originalApprovers: DirectoryObjectBase[] = [];
    approverChanges: ChangeRequestApprovers = {
        approvers: [],
        changeDate: new Date(),
        justification: ''
    };

    affectedObjectsDisplayColumns = ['id', 'displayName', 'idmManagedObjectType', 'idmPrincipalName'];

    requestDisplayObject: IRequestDetailsDisplayObject;
    requestDisplayProperties: string[] = [];

    get WorkflowRequestTypes() { return WorkflowRequestTypes; }
    get WorkflowRequestStates() { return WorkflowStates; }

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private authService: CalAngularService,
        private workflowRequestService: WorkflowRequestService,
        private directoryUserService: DirectoryUserService,
        private graphAppService: GraphApplicationService,
        private userService: GraphUserService,
        private dialog: MatDialog) { }
    ngOnChanges(changes: SimpleChanges): void {
        // if we haven't loaded before and the startSearch flag is set and we haven't already started searching
        // then start already!
        if (!this.doneLoading && changes.startSearch.currentValue && !this.isSearching) {
            this.getDetails();
        }
    }

    async getDetails() {
        this.isSearching = true;

        if (await lastValueFrom(this.authService.isUserSignedIn())) {
            this.currentUserProfile = this.authService.cvxClaimsPrincipal;
        }

        this.route.params.subscribe(params => {
            this.type = this.type ?? params['type'];
            this.id = this.id ?? params['id'];

            if (this.type == WorkflowRequestTypes.CyberEventCompromisedSourcingCompanyCreate) {
                this.affectedObjectsDisplayColumns = [...this.affectedObjectsDisplayColumns, 'accountEnabled'];
            }

            this.loadWorkflowRequest();
        });
    }

    async ngOnInit(): Promise<void> {
        this.hasRequestReaderRole = this.userService.CheckIsRequestReadAll(this.authService.cvxClaimsPrincipal);
        var routeParams = this.route.snapshot.params;
        // if input startSearch or route indicates a type and id
        // we should start searching
        if (this.startSearch || (routeParams['type'] && routeParams['id'])) {
            await this.getDetails();
            if ((routeParams['type'] && routeParams['id'])) {
                this.isDirectUrlToRequest = true;
            }

            if (this.authService.cvxClaimsPrincipal.roles.some(r => r === AppRoleConstants.RequestApproversManageAll)) {
                this.userIsRequestApproverManager = true;
            }
        }
    }

    workflowObserver = {
        next: (x: IGenericApiResponse<IWorkflowRequest>) => {
            this.workflowRequest = x.data;
            this.workflowRequest.affectedObjects = this.workflowRequest.affectedObjects ?? [];
            this.checkCanCancel()
        },
        error: (err: any) => { },
        complete: async () => {
            switch (this.workflowRequest.requestor.type) {
                case GroupMemberType.ServicePrincipal:
                    this.loadServicePrincipal(this.workflowRequest.requestor.id, this.requestor);
                    break;
                case GroupMemberType.User:
                    this.loadUsers([{ id: this.workflowRequest.requestor.id, domain: DirectoryDomain.Chevron } as IIdAndDomain], this.requestor);
                    break;
            }

            let approvers = this.workflowRequest.authorizedApprovers;
            await this.loadUsers(approvers.map(aa => { return { id: aa.id, domain: DirectoryDomain.Chevron }; }), this.approvers);
            this.originalApprovers = this.approvers.slice(); // save original approvers for comparison
            this.isDoneLoading();
        }
    }

    // cancel request
    checkCanCancel() {
        // Attestation escalations cannot be cancelled once submitted — the backend rejects it, so don't offer it here either.
        this.canCancel = this.type != WorkflowRequestTypes.Tier0AttestationEscalation
            && this.cancellableRequestStates.some(x => x == this.workflowRequest.state)
            && this.workflowRequest.requestor.id == this.authService.cvxClaimsPrincipal.objectId
    }

    cancelRequest() {
        this.isCancelling = true;
        this.cancellErrorMessage.message = "";
        this.cancellErrorMessage.errors = [];

        const observer = {
            next: (x: IGenericApiResponse<IWorkflowRequest>) => {
                this.workflowRequest = x.data;
                this.checkCanCancel()
                this.isDoneLoading();
            },
            error: (err: any) => {
                console.log(err)
                this.cancellErrorMessage.message = err?.error ?? err.statusText;
                this.cancellErrorMessage.errors = err?.error?.errors ?? [];
                this.isCancelling = false;
            },
            complete: () => {
                this.isCancelling = false;
            }
        };
        this.workflowRequestService.CancelRequest(this.type, this.id).subscribe(observer);
    }
    // load the initial workflow request
    loadWorkflowRequest() {
        this.workflowRequestService.GetById(this.type, this.id).subscribe(this.workflowObserver);
    }

    async loadUsers(ids: IIdAndDomain[], array: DirectoryObjectBase[]): Promise<void> {
        let users: DirectoryUser[] = [];
        for (let id of ids) {
            try {
                const user = await this.directoryUserService.GetByIdAsync(id);
                users.push(user);
            }
            catch (error) {
                users.push({ displayName: `unknown user with id ${id.id}`, mail: '' } as DirectoryUser);
            }
        }
        array.push.apply(array, users);
        this.isDoneLoading();
    }

    async loadServicePrincipal(id: string, array: DirectoryObjectBase[]): Promise<void> {
        try {
            let spResponse = await this.graphAppService.GetServicePrincipalAsync(id);
            let sp = { id: spResponse.data.id, displayName: spResponse.data.displayName } as DirectoryObjectBase;
            array.push(sp);
        }
        catch (error) {
            array.push({ displayName: `unknown service principal with id ${id}` } as DirectoryObjectBase);
        }
        this.isDoneLoading();
    }

    /**
     * handler for when the sub components have completed loading
     * @param val 
     * @param comp 
     */
    componentLoaded(val: boolean, comp: string) {
        switch (comp) {
            case 'approvals': this.doneLoadingApprovals = true; break;
            case 'details': this.doneLoadingDetails = true; break;
        }

        this.isDoneLoading();
    }

    /**
     * check if this and all sub-components have completed loading
     */
    isDoneLoading() {
        let doneLoadingRequestInfo = this.workflowRequest !== undefined &&
            this.workflowRequest.authorizedApprovers.length === this.approvers.length &&
            this.requestor.length > 0;

        if (doneLoadingRequestInfo) {
            this.requestDisplayObject = {
                id: this.workflowRequest?.id,
                type: this.workflowRequest?.type,
                state: this.workflowRequest?.state,
                comments: this.workflowRequest?.comments,
                createdDate: this.workflowRequest?.createDate,
                requestors: this.requestor.map(r => r.displayName),
                approvers: this.approvers.map(a => a.displayName),
                successes: this.workflowRequest?.successes ?? [],
                failures: this.workflowRequest?.failures ?? [],
                usingAdminRole: this.workflowRequest?.usingAdminRole ?? false,
                adminJustification: this.workflowRequest?.adminJustification ?? ''
            };

            let properties = ['id', 'type', 'state'];

            if (this.workflowRequest?.autoApproved || this.hasRequestReaderRole) {
                properties.push('comments');
            }

            properties.push('createdDate');
            properties.push('requestors');

            if (this.workflowRequest?.usingAdminRole) {
                properties.push('adminJustification');
            }

            if (this.requestDisplayObject.approvers.length !== 0) {
                properties.push('approvers');
            }

            if (this.requestDisplayObject.failures.length !== 0) {
                properties.push('failures');
            }

            this.requestDisplayProperties = properties;
        }

        if (doneLoadingRequestInfo && this.doneLoadingDetails && this.doneLoadingApprovals) {
            this.isSearching = false;
            this.doneLoading = true;
        }
    }
    exportToCsv() {
        const csvData = csvHelper.ConvertToCSV(this.workflowRequest.affectedObjects, this.affectedObjectsDisplayColumns);
        csvHelper.DownloadFile(csvData, `${this.workflowRequest?.id}-affected-objects-${new Date().toISOString()}`);
    }

    searchForPrimaryUser(replaceOriginalApprover: DirectoryObjectBase, currentApprover: DirectoryObjectBase): void {
        const dialogRef = this.dialog.open(DialogDirectorySearchComponent, {
            disableClose: true,
            autoFocus: true,
            maxWidth: 1000,
            width: '100%',
            data: {
                type: GroupMemberType.User,
                domain: DirectoryDomain.Chevron,
                userType: UserType.Primary,
                filterGroupRemoveNotAvailableAsGroupMember: false,
                filterGroupRemoveDynamicMembershipEnabled: false,
                filterGroupOnlyManagedByIdamp: false
            } as IDialogDirectoryData
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.approverChangeErrorMessage.message = '';
                this.approverChangeErrorMessage.errors = [];
                if (this.approvers.some(a => a.id === result.id)) {
                    this.approverChangeErrorMessage.message = `User ${result.displayName} is already an approver`;
                    return;
                }

                // update display
                const index = this.approvers.findIndex(a => a.id === currentApprover.id);
                if (index !== -1) {
                    this.approvers[index] = result;
                }

                // Find if there's an existing change for this original approver
                const existingChangeIndex = this.approverChanges.approvers.findIndex(
                    change => change.priorApprover.id === replaceOriginalApprover.id
                );

                // If found, update it with the new approver instead of adding a new change
                if (existingChangeIndex !== -1) {

                    // If user selects the original approver, remove this change
                    if (result.id === replaceOriginalApprover.id) {
                        // this is going back to the original approver, so remove the change
                        this.approverChanges.approvers.splice(existingChangeIndex, 1);
                        return;
                    }

                    // Update the existing change with the new approver
                    this.approverChanges.approvers[existingChangeIndex].newApprover = result;
                    return; // Skip adding a new change
                }

                // If not found, create a new change
                const approverChange: ApproverChange = {
                    priorApprover: replaceOriginalApprover,
                    newApprover: result
                };

                this.approverChanges.approvers.push(approverChange);
            }
        });
    }

    submitApproverUpdates(): void {
        if (new Set(this.approvers.map(a => a.id)).size !== this.approvers.length) {
            this.approverChangeSubmitErrorMessage.message = '';
            return;
        }

        this.approverChanges.changeDate = new Date();
        this.approverChanges.justification = this.approverChangeJustification.value;
        this.isSubmitting = true;


        const observer = {
            next: (x: IGenericApiResponse<IWorkflowRequest>) => {
                this.workflowRequest = x.data;
                this.isSubmitting = false

                // Reload the workflow request to refresh data
                this.resetAndReload();
            },
            error: (err: any) => {
                this.isSubmitting = false;
                this.approverChangeSubmitErrorMessage.message = err?.error ?? err.statusText;
                this.approverChangeSubmitErrorMessage.errors = err?.error?.errors ?? [];
            },
            complete: () => {
                this.isSubmitting = false;
            }
        };

        let createCall = this.workflowRequestService.UpdateRequestApprovers(this.type, this.id, this.approverChanges);
        createCall.subscribe(observer);
    }

    private resetAndReload() {
        this.router.navigated = false;
        this.router.navigate([this.router.url]);
    }
}

interface IRequestDetailsDisplayObject {
    id: string;
    type: string;
    state: string;
    comments: string;
    createdDate: Date;
    requestors: string[];
    approvers: string[];
    successes: string[];
    failures: string[];
    usingAdminRole: boolean;
    adminJustification: string;
}